import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ClipboardCheck, ShieldCheck, CheckCircle2, XCircle, Eye } from "lucide-react";
import { Link } from "react-router-dom";
import { listFarmerProfilesByStatus, verifyFarmerProfile, approveFarmerProfile, rejectFarmerProfile } from "@/lib/api";

const STATUS_TONES = {
  draft:     "bg-gray-100 text-gray-700",
  submitted: "bg-[var(--tr-citrus-yellow)]/25 text-[var(--tr-deep-green)]",
  verified:  "bg-[var(--tr-zest)]/20 text-[var(--tr-zest)]",
  approved:  "bg-[var(--tr-lime)]/30 text-[var(--tr-deep-green)]",
  rejected:  "bg-red-100 text-red-700",
};

export default function AdminFarmerRegistrations() {
  const { t } = useTranslation();
  const [status, setStatus] = useState("");
  const [items, setItems] = useState(null);

  const load = () => {
    listFarmerProfilesByStatus(status ? { status } : {}).then(setItems).catch(() => setItems([]));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const verify = async (id) => {
    if (!window.confirm(t("admin_p2.verify_confirm"))) return;
    try { await verifyFarmerProfile(id); toast.success(t("admin_p2.action_verify")); load(); }
    catch (err) { toast.error(err?.response?.data?.detail?.error || t("toast.failed")); }
  };
  const approve = async (id) => {
    if (!window.confirm(t("admin_p2.approve_confirm"))) return;
    try { await approveFarmerProfile(id); toast.success(t("admin_p2.action_approve")); load(); }
    catch (err) { toast.error(err?.response?.data?.detail?.error || t("toast.failed")); }
  };
  const reject = async (id) => {
    const reason = window.prompt(t("admin_p2.reject_prompt"));
    if (!reason || reason.trim().length < 3) return;
    try { await rejectFarmerProfile(id, reason.trim()); toast.success(t("admin_p2.action_reject_reg")); load(); }
    catch (err) { toast.error(err?.response?.data?.detail?.error || t("toast.failed")); }
  };

  const FILTERS = [
    { key: "", label: t("admin_p2.filter_all") },
    { key: "submitted", label: t("admin_p2.filter_submitted") },
    { key: "verified", label: t("admin_p2.filter_verified") },
    { key: "approved", label: t("admin_p2.filter_approved") },
    { key: "rejected", label: t("admin_p2.filter_rejected") },
    { key: "draft", label: t("admin_p2.filter_draft") },
  ];

  return (
    <div data-testid="admin-farmer-registrations">
      <div className="flex items-center gap-3 mb-2">
        <ClipboardCheck className="w-6 h-6 text-[var(--tr-deep-green)]" />
        <h2 className="tr-section-title text-3xl">{t("admin_p2.registrations_title")}</h2>
      </div>
      <p className="text-[var(--tr-muted)] leading-relaxed max-w-3xl">{t("admin_p2.registrations_body")}</p>

      {/* Status filter */}
      <div className="mt-6 flex flex-wrap gap-2">
        {FILTERS.map((s) => (
          <button
            key={s.key || "all"}
            data-testid={`reg-filter-${s.key || "all"}`}
            onClick={() => setStatus(s.key)}
            className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.14em] transition-colors ${
              status === s.key
                ? "bg-[var(--tr-deep-green)] text-white"
                : "bg-white border border-[rgba(6,78,59,0.15)] text-[var(--tr-deep-green)] hover:bg-[var(--tr-sage)]"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {!items ? (
        <div className="p-8 text-[var(--tr-muted)]">{t("common.loading")}</div>
      ) : items.length === 0 ? (
        <div data-testid="reg-empty" className="tr-card p-10 text-center text-[var(--tr-muted)] mt-6">
          {t("admin_p2.empty_registrations")}
        </div>
      ) : (
        <div className="tr-card overflow-hidden mt-6" data-testid="reg-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[var(--tr-sage)]/50">
                <tr>
                  {["Farmer ID", t("admin.col_name"), t("admin.col_phone"), "Village", "Land (Acres)", t("admin_p2.col_status")].map((h) => (
                    <th key={h} className="text-left px-4 py-3 font-semibold text-[var(--tr-deep-green)] uppercase text-[10px] tracking-[0.18em]">{h}</th>
                  ))}
                  <th className="text-right px-4 py-3 text-[var(--tr-deep-green)] uppercase text-[10px] tracking-[0.18em]">{t("common.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((f) => (
                  <tr key={f.id} data-testid={`reg-row-${f.farmer_code}`} className="border-t border-[rgba(6,78,59,0.08)] hover:bg-[var(--tr-bone)]">
                    <td className="px-4 py-3 font-mono font-semibold text-[var(--tr-deep-green)]">{f.farmer_code}</td>
                    <td className="px-4 py-3 font-semibold text-[var(--tr-deep-green)]">{f.full_name}</td>
                    <td className="px-4 py-3 font-mono">{f.phone}</td>
                    <td className="px-4 py-3">{f.village || "—"}</td>
                    <td className="px-4 py-3">{f.land_total_acres ?? "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${STATUS_TONES[f.registration_status] || "bg-gray-100 text-gray-700"}`}>
                        {f.registration_status || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Link to={`/admin/farmer-profiles/${f.id}`} data-testid={`reg-view-${f.farmer_code}`} className="inline-flex items-center gap-1 text-[var(--tr-deep-green)] text-sm font-semibold mr-3 hover:underline">
                        <Eye className="w-4 h-4" />
                      </Link>
                      {f.registration_status === "submitted" && (
                        <button data-testid={`reg-verify-${f.farmer_code}`} onClick={() => verify(f.id)} className="inline-flex items-center gap-1 text-[var(--tr-zest)] text-sm font-semibold mr-3 hover:underline">
                          <ShieldCheck className="w-4 h-4" /> {t("admin_p2.action_verify")}
                        </button>
                      )}
                      {f.registration_status === "verified" && (
                        <button data-testid={`reg-approve-${f.farmer_code}`} onClick={() => approve(f.id)} className="inline-flex items-center gap-1 text-[var(--tr-deep-green)] text-sm font-semibold mr-3 hover:underline">
                          <CheckCircle2 className="w-4 h-4" /> {t("admin_p2.action_approve")}
                        </button>
                      )}
                      {(f.registration_status === "submitted" || f.registration_status === "verified") && (
                        <button data-testid={`reg-reject-${f.farmer_code}`} onClick={() => reject(f.id)} className="inline-flex items-center gap-1 text-red-600 text-sm font-semibold hover:underline">
                          <XCircle className="w-4 h-4" /> {t("admin_p2.action_reject_reg")}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
